/**
 * KPI Card Component
 * Displays key performance indicators with optional trends and actions
 */

import { LucideIcon, TrendingUp, TrendingDown, ArrowRight } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { ReactNode } from 'react'

interface KPICardProps {
  title: string
  value?: string | number
  icon: LucideIcon
  description?: string | ReactNode
  trend?: { value: number; isPositive: boolean }
  urgent?: boolean
  onClick?: () => void
  className?: string
  variant?: 'default' | 'compact' | 'detailed'
  loading?: boolean
}

export function KPICard({
  title,
  value,
  icon: Icon,
  description,
  trend,
  urgent,
  onClick,
  className,
  variant = 'default',
  loading = false,
}: KPICardProps) {
  const formattedValue =
    typeof value === 'number' ? value.toLocaleString() : value

  if (loading) {
    return (
      <Card className={cn('animate-pulse', className)}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="h-4 bg-muted rounded w-24 mb-2" />
              <div className="h-8 bg-muted rounded w-16" />
            </div>
            <div className="h-10 w-10 bg-muted rounded-full" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card
      className={cn(
        'group transition-all duration-200',
        onClick && 'cursor-pointer hover:shadow-md hover:border-primary/30',
        urgent && 'border-orange-300 bg-gradient-to-br from-orange-50 to-white',
        className
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            {/* Title */}
            <p className="text-sm font-medium text-muted-foreground mb-1 truncate">
              {title}
            </p>
            
            {/* Value Row */}
            <div className="flex items-baseline gap-2 flex-wrap">
              {value !== undefined && value !== null && (
                <span className={cn(
                  'font-bold tracking-tight',
                  variant === 'compact' ? 'text-xl' : 'text-2xl lg:text-3xl',
                  urgent && 'text-orange-600'
                )}>
                  {formattedValue}
                </span>
              )}
              
              {/* Trend Indicator */}
              {trend && (
                <span
                  className={cn(
                    'inline-flex items-center text-xs font-medium px-1.5 py-0.5 rounded-full',
                    trend.isPositive 
                      ? 'text-green-700 bg-green-100' 
                      : 'text-red-700 bg-red-100'
                  )}
                >
                  {trend.isPositive ? (
                    <TrendingUp className="h-3 w-3 me-0.5" />
                  ) : (
                    <TrendingDown className="h-3 w-3 me-0.5" />
                  )}
                  {trend.isPositive ? '+' : ''}{trend.value}%
                </span>
              )}
            </div>
            
            {/* Description */}
            {description && (
              <div className="text-xs text-muted-foreground mt-1 truncate">
                {description}
              </div>
            )}
          </div>
          
          {/* Icon */}
          <div className={cn(
            'flex-shrink-0 ms-3 p-2.5 rounded-full transition-colors',
            urgent 
              ? 'bg-orange-100 text-orange-600'
              : 'bg-primary/10 text-primary group-hover:bg-primary/20'
          )}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
        
        {/* Click indicator */}
        {onClick && (
          <div className="flex items-center justify-end mt-2 pt-2 border-t border-transparent group-hover:border-border/50 transition-all">
            <span className="text-xs text-muted-foreground group-hover:text-primary flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              View details
              <ArrowRight className="h-3 w-3" />
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ==================== Compact KPI Card ====================

interface CompactKPICardProps {
  title: string
  value: string | number
  icon: LucideIcon
  color?: 'default' | 'green' | 'blue' | 'orange' | 'red' | 'purple'
  onClick?: () => void
}

export function CompactKPICard({
  title,
  value,
  icon: Icon,
  color = 'default',
  onClick,
}: CompactKPICardProps) {
  const colorClasses = {
    default: 'bg-gray-50 text-gray-700 border-gray-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    purple: 'bg-brand-primary/10 text-brand-primary border-brand-primary/30',
  }

  const iconColorClasses = {
    default: 'text-gray-500',
    green: 'text-green-500',
    blue: 'text-blue-500',
    orange: 'text-orange-500',
    red: 'text-red-500',
    purple: 'text-brand-primary',
  }

  return (
    <Card 
      className={cn(
        'border transition-all',
        colorClasses[color],
        onClick && 'cursor-pointer hover:shadow-sm'
      )}
      onClick={onClick}
    >
      <CardContent className="p-3 flex items-center gap-3">
        <Icon className={cn('h-5 w-5 flex-shrink-0', iconColorClasses[color])} />
        <div className="min-w-0 flex-1">
          <p className="text-lg font-bold truncate">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          <p className="text-xs truncate">{title}</p>
        </div>
      </CardContent>
    </Card>
  )
}
